import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import analytics from "web/script/analytics/analytics";

type MenuCategoryType = "product_type" | "product_category" | "featured_category";

type MenuType = "desktop" | "mobile" | null;

interface NavigationContextProps {
    showMenu: MenuType;
    isSearchOpen: boolean;
    setShowMenu: React.Dispatch<React.SetStateAction<MenuType>>;
    setIsSearchOpen: React.Dispatch<React.SetStateAction<boolean>>;
    handleMenuOpenedAnalytics: (menuState: "opened" | "closed", targetGender: string) => void;
    handleCategoryAnalytics: (
        categoryType: MenuCategoryType,
        action?: "clicked" | "impression"
    ) => void;
    handleHeaderAnalytics: (category: string, buttonName: string) => void;
    handleNavigationAnalytics: (navButtonName: string) => void;
    hideGenderControl: boolean;
    setHideGenderControl: React.Dispatch<React.SetStateAction<boolean>>;
}
interface NavigationContextProviderProps {
    children: React.ReactNode;
}

const NavigationContext = createContext<NavigationContextProps>({
    showMenu: null,
    isSearchOpen: false,
    setShowMenu: () => {},
    setIsSearchOpen: () => {},
    handleMenuOpenedAnalytics: () => {},
    handleNavigationAnalytics: () => {},
    handleHeaderAnalytics: () => {},
    handleCategoryAnalytics: () => {},
    hideGenderControl: false,
    setHideGenderControl: () => {},
});

export function NavigationContextProvider({
    children,
}: NavigationContextProviderProps): React.ReactElement {
    const [showMenu, setShowMenu] = useState<MenuType>(null);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [hideGenderControl, setHideGenderControl] = useState(false);

    const { isDesktopViewport } = useDomViewport();
    const handleMenuOpenedAnalytics = useCallback(function (menuState, targetGender): void {
        analytics.event("top_nav", menuState, `nav_${targetGender.toLowerCase()}_menu`);
    }, []);

    const handleCategoryAnalytics = useCallback(function (categoryType, action = "clicked"): void {
        analytics.event("top_nav", action, categoryType);
    }, []);

    const handleNavigationAnalytics = useCallback(function (navButtonName): void {
        analytics.event("top_nav", "clicked", navButtonName);
    }, []);

    const handleHeaderAnalytics = useCallback(function (category, buttonName): void {
        analytics.event(category, "clicked", buttonName);
    }, []);
    /** Auto hides mobile search if viewport changes to desktop*/
    useEffect(() => {
        if (isDesktopViewport) {
            setIsSearchOpen(false);
        }
    }, [isDesktopViewport]);

    const value = {
        showMenu,
        setShowMenu,
        isSearchOpen,
        setIsSearchOpen,
        handleMenuOpenedAnalytics,
        handleCategoryAnalytics,
        handleNavigationAnalytics,
        handleHeaderAnalytics,
        hideGenderControl,
        setHideGenderControl,
    };
    return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigationContext(): NavigationContextProps {
    return useContext(NavigationContext);
}
